import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';

import { getPlatformDisplayName } from 'src/utils/platform-label';
import { isUnconfirmedCreate } from 'src/utils/extension-publish-rules';
import { describeFailure, mapPlatformError } from 'src/utils/platform-error-message';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

import { parsePlatformResultKey } from '../utils/platform-keys';
import { useBulkCrossListRun } from '../context/bulk-cross-list-run-context';
import { platformRowLabel, describePlatformRow, describeUnconfirmedRow } from '../utils/run-display';

// The floating panel that shows the bulk cross-listing run in progress, wherever the user is in
// the app. It only reads the run — starting, stopping and retrying all live in the run context.

const STATUS_DISPLAY = {
  queued: { icon: 'solar:clock-circle-bold', color: 'text.secondary', text: 'Listing your items…' },
  processing: { icon: 'solar:refresh-bold', color: 'info.main', text: 'Listing your items…' },
  completed: { icon: 'solar:check-circle-bold', color: 'success.main', text: 'Done' },
  completed_with_errors: {
    icon: 'solar:danger-triangle-bold',
    color: 'warning.main',
    text: 'Done, some items need attention',
  },
  needs_extension: {
    icon: 'solar:plug-circle-bold',
    color: 'warning.main',
    text: 'Needs the Voolist extension',
  },
  paused: { icon: 'solar:pause-circle-bold', color: 'warning.main', text: 'Paused' },
  stopped: {
    icon: 'solar:stop-circle-bold',
    color: 'warning.main',
    text: 'Stopped, some items weren’t listed',
  },
  expired: {
    icon: 'solar:danger-triangle-bold',
    color: 'text.secondary',
    text: 'These results have expired',
  },
  not_found: {
    icon: 'solar:danger-triangle-bold',
    color: 'text.secondary',
    text: 'These results have expired',
  },
};

function describeRun(overallStatus, progress) {
  if (overallStatus !== 'creating_via_extension') {
    return STATUS_DISPLAY[overallStatus] || STATUS_DISPLAY.queued;
  }
  if (!progress.total || !progress.currentPlatform) {
    return { icon: 'solar:refresh-bold', color: 'info.main', text: 'Getting started…' };
  }
  return {
    icon: 'solar:refresh-bold',
    color: 'info.main',
    text: `Listing on ${getPlatformDisplayName(progress.currentPlatform)} (${Math.min(progress.current, progress.total)} of ${progress.total})…`,
  };
}

function headerColor(overallStatus) {
  if (overallStatus === 'completed') return 'success.lighter';
  if (['completed_with_errors', 'paused', 'stopped', 'needs_extension'].includes(overallStatus)) return 'warning.lighter';
  if (overallStatus === 'expired' || overallStatus === 'not_found') return 'background.neutral';
  return 'info.lighter';
}

// How one item of the run reads at a glance. One function so the icon and the card behind it can
// never tell different stories — they did, and an item nobody could account for showed a green
// tick because only the failures were being looked at.
function listingStatus(result) {
  if ((result.pendingCount || 0) > 0) {
    return { icon: 'solar:clock-circle-bold', color: 'info.main', background: 'info.lighter' };
  }
  if (result.failureCount > 0) {
    return { icon: 'solar:close-circle-bold', color: 'error.main', background: 'error.lighter' };
  }
  // Nothing failed, but a marketplace we cannot account for still wants the seller to look.
  if ((result.unconfirmedCount || 0) > 0) {
    return { icon: 'solar:clock-circle-bold', color: 'warning.main', background: 'warning.lighter' };
  }
  return { icon: 'solar:check-circle-bold', color: 'success.main', background: 'success.lighter' };
}

// What one platform (or one account on a platform) ended up doing for one listing.
function PlatformResultRow({ resultKey, platformResult, marketplaces, isCreating, isStopped }) {
  const { platform } = parsePlatformResultKey(resultKey);
  const label = platformRowLabel(resultKey, platformResult, marketplaces);

  if (platformResult.pendingExtension) {
    // Nothing is still on its way once the user has stopped the run.
    if (isStopped) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}: Not listed, you pressed Stop
        </Typography>
      );
    }

    return (
      <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
        {label}: {isCreating ? 'Listing…' : 'Up next'}
      </Typography>
    );
  }

  // The post may be live on the marketplace, so this is amber and not red: nothing failed, the
  // seller just has to go and look. The full explanation is on the run's own page.
  if (isUnconfirmedCreate(platformResult)) {
    const notice = describeUnconfirmedRow(platformResult, label);
    // The heading comes from describePlatformRow, the same place the run's own page reads it, so
    // the two views cannot end up calling one state by two different names.
    return (
      <Box>
        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
          {label}: {describePlatformRow(platformResult).label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
          {notice.message}
        </Typography>
      </Box>
    );
  }

  // A failure in one line: the widget has no Retry, and the run's own page carries the full advice.
  if (!platformResult.success) {
    const failure = mapPlatformError(platformResult.error, platform);
    return (
      <Box>
        <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
          {label}: {failure.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
          {describeFailure(failure)}
        </Typography>
      </Box>
    );
  }

  // Nothing was posted because the item sold out: grey, not a green success, and named the way the
  // run's own page names it.
  if (platformResult.soldOut) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}: {describePlatformRow(platformResult).label}
      </Typography>
    );
  }

  const getSuccessText = () => {
    if (platformResult.alreadyCreated) return 'Already listed';
    if (platformResult.extensionCreated) return 'Listed';
    return platformResult.skipped ? 'Already listed' : 'Listed';
  };

  return (
    <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
      {label}: {getSuccessText()}
    </Typography>
  );
}

PlatformResultRow.propTypes = {
  isCreating: PropTypes.bool,
  isStopped: PropTypes.bool,
  marketplaces: PropTypes.array,
  platformResult: PropTypes.object.isRequired,
  resultKey: PropTypes.string.isRequired,
};

function ResultsList({ results, marketplaces, isCreating, isStopped }) {
  return (
    <Card variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
      <Stack spacing={0.5} sx={{ p: 1 }}>
        {results.map((result, index) => {
          const status = listingStatus(result);
          return (
            <Box
              key={result.listingId || index}
              sx={{ p: 1, borderRadius: 1, bgcolor: status.background }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon={status.icon} width={16} sx={{ color: status.color }} />
                <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1 }}>
                  {result.listingTitle || 'Untitled item'}
                </Typography>
              </Stack>

              <Box sx={{ mt: 0.5, pl: 3 }}>
                {Object.keys(result.results || {}).length === 0 ? (
                  // A listing whose whole job failed comes back with no marketplace results at
                  // all, so without this line its card would be a red count and nothing else.
                  <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                    Couldn&apos;t list this item
                  </Typography>
                ) : (
                  Object.entries(result.results).map(([resultKey, platformResult]) => (
                    <PlatformResultRow
                      key={resultKey}
                      resultKey={resultKey}
                      platformResult={platformResult}
                      marketplaces={marketplaces}
                      isCreating={isCreating}
                      isStopped={isStopped}
                    />
                  ))
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Card>
  );
}

ResultsList.propTypes = {
  isCreating: PropTypes.bool,
  isStopped: PropTypes.bool,
  marketplaces: PropTypes.array,
  results: PropTypes.array.isRequired,
};

export default function BulkCrossListWidget() {
  const {
    activeRun,
    serverStatus,
    extension,
    mergedResults,
    counts,
    overallStatus,
    isStopped,
    dismissRun,
    cancelExtension,
  } = useBulkCrossListRun();

  const { marketplaces } = useSelector((state) => state.management);
  const router = useRouter();
  const { pathname } = useLocation();
  const [isMinimized, setIsMinimized] = useState(false);
  const confirmStop = useBoolean();

  if (!activeRun) return null;

  // The run's own page shows everything this panel does and more, so the panel keeps out of its
  // way while the user is on it.
  const batchPage = matchPath('/listing/bulk-cross-list/batch/:batchId', pathname);
  if (batchPage?.params?.batchId === activeRun.batchId) return null;

  const isCreating = overallStatus === 'creating_via_extension';
  const isPausedRun = overallStatus === 'paused';
  const isStoppedRun = overallStatus === 'stopped';
  // Paused counts as extension work in progress: the numbers stay on screen, they just stop moving.
  const isExtensionActive = isCreating || isPausedRun;
  const isServerWorking = overallStatus === 'processing' || overallStatus === 'queued';
  const isFinished = ['completed', 'completed_with_errors', 'stopped'].includes(overallStatus);

  const serverProgress = serverStatus?.progress || 0;
  const totalListings = serverStatus?.total || activeRun.listingCount || 0;
  const completedListings = serverStatus?.completed || 0;

  const { progress } = extension;
  // The current operation is counted as started, not finished, so the bar shows the work behind
  // it rather than jumping ahead of the listing being created right now. Clamped at zero because
  // `current` is still 0 in the moment between the run being staged and the first create starting.
  const creatingProgress = progress.total > 0
    ? Math.max(0, Math.round(((progress.current - 1) / progress.total) * 100))
    : 0;

  const status = describeRun(overallStatus, progress);

  const pillText = () => {
    if (isCreating) return `Cross-listing (${progress.current} of ${progress.total})…`;
    if (isPausedRun) return `Cross-listing paused (${progress.current} of ${progress.total})`;
    if (isStoppedRun) return 'Cross-listing stopped';
    return `Cross-listing ${isFinished ? 'done' : `${serverProgress}%`}`;
  };
  const extensionRunning = extension.phase === 'processing' || extension.phase === 'paused';

  const handleClose = () => {
    if (extensionRunning) confirmStop.onTrue();
    else dismissRun();
  };

  const handleConfirmStop = () => {
    cancelExtension();
    confirmStop.onFalse();
    dismissRun();
  };

  const handleViewDetails = () => {
    router.push(paths.dashboard.listing.bulkCrossListBatch(activeRun.batchId));
  };

  const stopConfirmation = (
    <ConfirmDialog
      open={confirmStop.value}
      onClose={confirmStop.onFalse}
      title="Stop listing the rest of your items?"
      content="Items already listed stay live. You can cross-list the rest later."
      action={
        <Button variant="contained" color="error" onClick={handleConfirmStop}>
          Stop
        </Button>
      }
    />
  );

  if (isMinimized) {
    return (
      <>
        <Slide direction="up" in mountOnEnter unmountOnExit>
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1300,
              borderRadius: 2,
              overflow: 'hidden',
              minWidth: 280,
            }}
          >
            <Box
              sx={{
                p: 1.5,
                bgcolor: headerColor(overallStatus),
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
              onClick={() => setIsMinimized(false)}
            >
              {(isServerWorking || isCreating) && <CircularProgress size={16} />}
              {!isServerWorking && !isCreating && (
                <Iconify icon={status.icon} width={20} sx={{ color: status.color }} />
              )}
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                {pillText()}
              </Typography>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}>
                <Iconify icon="eva:expand-outline" width={18} />
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
                <Iconify icon="eva:close-outline" width={18} />
              </IconButton>
            </Box>
          </Paper>
        </Slide>

        {stopConfirmation}
      </>
    );
  }

  return (
    <>
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1300,
            borderRadius: 2,
            overflow: 'hidden',
            width: 360,
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              p: 2,
              bgcolor: headerColor(overallStatus),
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Iconify icon={status.icon} width={24} sx={{ color: status.color }} />
            <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
              Cross-listing
            </Typography>
            <IconButton size="small" onClick={() => setIsMinimized(true)}>
              <Iconify icon="eva:minus-outline" width={18} />
            </IconButton>
            <IconButton size="small" onClick={handleClose}>
              <Iconify icon="eva:close-outline" width={18} />
            </IconButton>
          </Box>

          {isServerWorking && (
            <LinearProgress variant="determinate" value={serverProgress} sx={{ height: 4 }} />
          )}
          {isExtensionActive && (
            <LinearProgress variant="determinate" value={creatingProgress} sx={{ height: 4 }} />
          )}

          <CardContent sx={{ p: 2, overflow: 'auto', flex: 1 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {status.text}
                </Typography>
                {!isExtensionActive && totalListings > 0 && (
                  <Typography variant="body2" fontWeight={600}>
                    {completedListings} / {totalListings} listings
                  </Typography>
                )}
              </Box>

              {isExtensionActive && progress.currentListing && (
                <Typography variant="caption" color="text.secondary">
                  {progress.currentListing}
                </Typography>
              )}

              {mergedResults.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    icon={<Iconify icon="solar:check-circle-bold" width={16} />}
                    label={`${counts.totalSuccess} listed`}
                    color="success"
                    variant="soft"
                  />
                  {counts.totalPending > 0 && (
                    <Chip
                      size="small"
                      icon={<Iconify icon="solar:clock-circle-bold" width={16} />}
                      label={`${counts.totalPending} up next`}
                      color="info"
                      variant="soft"
                    />
                  )}
                  {counts.totalFailed > 0 && (
                    <Chip
                      size="small"
                      icon={<Iconify icon="solar:close-circle-bold" width={16} />}
                      label={`${counts.totalFailed} failed`}
                      color="error"
                      variant="soft"
                    />
                  )}
                  {counts.totalUnconfirmed > 0 && (
                    <Chip
                      size="small"
                      icon={<Iconify icon="solar:clock-circle-bold" width={16} />}
                      label={`${counts.totalUnconfirmed} to check`}
                      color="warning"
                      variant="soft"
                    />
                  )}
                </Box>
              )}

              {mergedResults.length > 0 && (
                <ResultsList
                  results={mergedResults}
                  marketplaces={marketplaces}
                  isCreating={isCreating}
                  isStopped={isStopped}
                />
              )}
            </Stack>
          </CardContent>

          <Box sx={{ p: 2, pt: 0 }}>
            <Stack direction="row" spacing={1}>
              <Button fullWidth variant="outlined" onClick={handleViewDetails}>
                View details
              </Button>

              {isFinished && (
                <Button fullWidth variant="contained" onClick={dismissRun}>
                  Done
                </Button>
              )}
            </Stack>
          </Box>
        </Paper>
      </Slide>

      {stopConfirmation}
    </>
  );
}
